import React, { FocusEvent, KeyboardEvent, MouseEvent, useState } from "react"
import { useFormContext } from "react-hook-form"
import type { TextInputProps } from "@mantine/core"
import { TextInput } from "@mantine/core"

import { useStyles } from "@/components/Controlled/TitleInput/styles"

export interface TitleInputProps extends TextInputProps {
    defaultValue?: string
    blankValue?: string
    className?: string
    disabled?: boolean
}

enum KeyboardKeys {
    ENTER = "Enter",
    ESC = "Escape",
    BACKSPACE = " ",
}

export const TitleInput = ({
    defaultValue,
    blankValue,
    className,
    onKeyDown,
    onBlur,
    onClick,
    ...props
}: TitleInputProps) => {
    const { classes } = useStyles()

    const formContext = useFormContext()
    const value = formContext.watch(props.name)

    const [cachedValue, setCachedValue] = useState(value ?? defaultValue)

    const handleClick = (e: MouseEvent<HTMLInputElement>) => {
        e.stopPropagation()
        if (onClick) onClick(e)
    }

    const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (onKeyDown) onKeyDown(e)

        const isEnterPressed = e.key === KeyboardKeys.ENTER
        const isEscPressed = e.key === KeyboardKeys.ESC
        const isBackSpacePressed = e.key === KeyboardKeys.BACKSPACE

        const isMappedKeyPressed = isEnterPressed || isEscPressed || isBackSpacePressed

        const target = e.target as HTMLInputElement

        if (!isMappedKeyPressed) return
        if (isBackSpacePressed) formContext.setValue(props.name, target.value + " ")
        if (isEscPressed) formContext.setValue(props.name, cachedValue)
        if (isEscPressed || isEnterPressed) target.blur()

        e.preventDefault()
    }

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        if (onBlur) onBlur(e)

        if (value) return setCachedValue(value)

        formContext.setValue(props.name, blankValue)
        setCachedValue(blankValue)
    }

    return (
        <TextInput
            onBlur={handleBlur}
            onClick={handleClick}
            onKeyDown={handleKeyPress}
            className={`${className} ${classes.input}`}
            {...props}
        />
    )
}
